import { useMemo } from 'react';
import { Company } from '../../types/career';

interface BuildingProps {
  company: Company;
  isNearby: boolean;
  onClick: () => void;
  side: 'top' | 'bottom';
}

const Building = ({ company, isNearby, onClick, side }: BuildingProps) => {
  const baseHeight = company.buildingHeight || 150;
  const buildingColor = company.buildingColor || '#607D8B';

  // Generate windows
  const windowRows = Math.floor(baseHeight / 40);
  const windowsPerRow = 3;

  // Generate stable window opacities based on company id and window index
  const windowOpacities = useMemo(() => {
    const totalWindows = windowRows * windowsPerRow;
    return Array.from({ length: totalWindows }, (_, i) => {
      // Use a simple hash based on company id and index for deterministic "randomness"
      const hash = (company.id.charCodeAt(i % company.id.length) + i * 17) % 10;
      return hash > 3 ? 0.9 : 0.4;
    });
  }, [company.id, windowRows, windowsPerRow]);

  const formatShortDate = (dateStr: string) => {
    const [year, month] = dateStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const dateRange = `${formatShortDate(company.startDate)} - ${company.endDate ? formatShortDate(company.endDate) : 'Present'}`;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };

  return (
    <div
      className={`relative cursor-pointer transition-all duration-300 ${
        isNearby ? 'z-20' : 'z-10'
      }`}
      style={{ width: '150px' }}
      onClick={handleClick}
    >
      {/* Building structure */}
      <div
        className={`relative transition-all duration-300 ${
          isNearby ? 'ring-4 ring-yellow-400 ring-opacity-75' : ''
        }`}
        style={{
          height: `${baseHeight}px`,
          backgroundColor: buildingColor,
          borderRadius: side === 'top' ? '8px 8px 0 0' : '0 0 8px 8px',
          boxShadow: isNearby
            ? `0 0 20px ${buildingColor}80, 0 4px 15px rgba(0,0,0,0.3)`
            : '0 4px 10px rgba(0,0,0,0.2)',
        }}
      >
        {/* Building facade - darker shade */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, transparent 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.2) 100%)`,
            borderRadius: 'inherit',
          }}
        />

        {/* Windows */}
        <div className="absolute inset-4 grid gap-2" style={{ gridTemplateColumns: `repeat(${windowsPerRow}, 1fr)` }}>
          {windowOpacities.map((opacity, i) => (
            <div
              key={i}
              className="bg-yellow-200 rounded-sm"
              style={{
                height: '20px',
                opacity,
                boxShadow: 'inset 0 0 4px rgba(0,0,0,0.3)',
              }}
            />
          ))}
        </div>

        {/* Door at bottom for top-side buildings, top for bottom-side */}
        <div
          className="absolute left-1/2 -translate-x-1/2 bg-amber-800 rounded-t-lg"
          style={{
            width: '30px',
            height: '40px',
            ...(side === 'top' ? { bottom: 0 } : { top: 0, borderRadius: '0 0 8px 8px' }),
          }}
        >
          {/* Door handle */}
          <div
            className="absolute bg-yellow-400 rounded-full"
            style={{
              width: '4px',
              height: '4px',
              right: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />
        </div>

        {/* Company name sign */}
        <div
          className="absolute left-1/2 -translate-x-1/2 bg-white px-3 py-1 rounded-lg shadow-md text-center whitespace-nowrap flex flex-col"
          style={{
            ...(side === 'top' ? { top: '-40px' } : { bottom: '-40px' }),
          }}
        >
          <span className="text-base font-bold text-gray-800 truncate" style={{ lineHeight: 1.2 }}>{company.name}</span>
          <span className="text-base text-gray-500" style={{ lineHeight: 1.2 }}>{dateRange}</span>
        </div>
      </div>
    </div>
  );
};

export default Building;
